import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { HiPencil, HiTrash, HiPlus } from "react-icons/hi";
import { DataTable, PageLayout, ActionButton } from "../../../components/modern";
import type { Column } from "../../../components/modern";
import { hasPermission } from "../../../utils/permissions";
import { useAuth } from "../../../contexts/AuthContext";
import { adminApi } from "../../../utils/api";
import { showErrorToast, showSuccessToast } from "../../../utils/toastNotifications";
import './CategoryIndex.scss';

interface Category {
  id: number;
  name: string;
  hsCode: string;
  description: string;
  tax: number;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export const CategoryIndex: React.FC = () => {
  const { t, i18n } = useTranslation(['categories', 'common']);
  const navigate = useNavigate();
  const { token } = useAuth();
  const isRTL = i18n.language === 'ar';

  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState<Record<string, string>>({});

  const buildQuery = (page: number, size: number) => {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: size.toString()
    });

    Object.entries(filters).forEach(([key, value]) => {
      const trimmed = value.trim();
      if (!trimmed) return;
      params.append(key, trimmed);
    });

    return params.toString();
  };

  const fetchCategories = async (page: number, size: number) => {
    try {
      setLoading(true);
      const query = buildQuery(page, size);
      const response = await adminApi.call(`/category?${query}`, 'GET');

      if (!response.success) {
        showErrorToast(response.error || t('categories:messages.fetchError'));
        return;
      }

      setCategories(response.data?.items || []);
      setTotalItems(response.data?.totalItems || 0);
    } catch (error) {
      showErrorToast(t('categories:messages.fetchError'));
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories(currentPage, pageSize);
  }, [currentPage, pageSize, filters]);

  const deleteCategory = async (id: number) => {
    if (!window.confirm(t('categories:messages.deleteConfirm'))) {
      return;
    }

    try {
      const response = await adminApi.call(`/category/${id}`, 'DELETE');

      if (!response.success) {
        showErrorToast(response.error || t('categories:messages.deleteError'));
        return;
      }

      showSuccessToast(t('categories:messages.deleteSuccess'));
      fetchCategories(currentPage, pageSize);
    } catch (error) {
      showErrorToast(t('categories:messages.deleteError'));
      console.error('Error deleting category:', error);
    }
  };

  const handleCreateCategory = () => {
    navigate('/categories/create');
  };

  const handleEditCategory = (id: number) => {
    navigate(`/categories/edit/${id}`);
  };

  const columns: Column[] = [
    {
      key: 'name',
      title: t('categories:table.name'),
      sortable: true,
      filterable: true,
      filterKey: 'name',
    },
    {
      key: 'hsCode',
      title: t('categories:table.hsCode'),
      sortable: true,
      filterable: true,
      filterKey: 'hsCode',
    },
    {
      key: 'description',
      title: t('categories:table.description'),
      sortable: false,
      filterable: true,
      filterKey: 'description',
    },
    {
      key: 'tax',
      title: t('categories:table.tax'),
      sortable: true,
      render: (value) => `${value}%`,
    },
    {
      key: 'actions',
      title: t('categories:table.actions'),
      sortable: false,
      width: '150px',
      align: 'center',
      render: (_, category) => (
        <div className="action-buttons">
          {hasPermission("Update Category") && (
            <ActionButton
              variant="warning"
              size="sm"
              icon={HiPencil}
              onClick={() => handleEditCategory(category.id)}
              tooltip={t('categories:editCategory')}
            />
          )}
          {hasPermission("Delete Category") && (
            <ActionButton
              variant="danger"
              size="sm"
              icon={HiTrash}
              onClick={() => deleteCategory(category.id)}
              tooltip={t('categories:deleteCategory')}
            />
          )}
        </div>
      ),
    },
  ];

  const breadcrumbs = [
    { label: t('categories:breadcrumbs.categories') }
  ];

  return (
    <PageLayout
      title={t('categories:title')}
      subtitle={t('categories:subtitle')}
      breadcrumbs={breadcrumbs}
      className={isRTL ? 'rtl' : 'ltr'}
      isIndexPage={true}
    >
      <DataTable
        columns={columns}
        data={categories}
        loading={loading}
        searchable={true}
        filterable={true}
        exportable={true}
        refreshable={true}
        addButton={hasPermission("Create Category") ? {
          label: t('categories:addNew'),
          onClick: handleCreateCategory
        } : undefined}
        paginationMode="server"
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        pageSizeOptions={[10, 20, 50]}
        showPageSizeSelector={true}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
        onFilterChange={(key, value) => {
          setFilters(prev => ({ ...prev, [key]: value }));
          setCurrentPage(1);
        }}
        onRefresh={() => fetchCategories(currentPage, pageSize)}
        emptyMessage={t('categories:messages.noCategories')}
      />
    </PageLayout>
  );
};
